import { Box, TextField, Button, ClickAwayListener } from "@mui/material";
import {
  DateCalendar,
  LocalizationProvider,
  StaticTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import isBetweenPlugin from "dayjs/plugin/isBetween";

dayjs.extend(isBetweenPlugin);

const TimeDatePicker = ({
  time, // This prop controls whether time selection is enabled
  startDateReFormat,
  endDateReFormat,
  handleStartDate,
  handleEndDate,
}) => {
  const [open, setOpen] = useState(false);
  const [selecting, setSelecting] = useState("start");
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [tempDate, setTempDate] = useState(null); // Stores the date part temporarily
  const [timeView, setTimeView] = useState("hours");

  // Local calendar state to prevent auto-reset
  const [calendarMonth, setCalendarMonth] = useState(dayjs());

  // Formats the date range string
  const formatDateRange = () => {
    const format = time ? "DD/MM/YYYY hh:mm A" : "DD/MM/YYYY";
    const isValid = (date) => date && dayjs(date).isValid();
    const startValid = isValid(startDateReFormat);
    const endValid = isValid(endDateReFormat);

    if (startValid && !endValid) {
      return `${dayjs(startDateReFormat).format(format)} - End Date`;
    }

    if (startValid && endValid) {
      return `${dayjs(startDateReFormat).format(format)} - ${dayjs(
        endDateReFormat
      ).format(format)}`;
    }

    return "Date Range";
  };

  const handleDateSelect = (date) => {
    const selectedDate = dayjs(date);

    if (time) { // If time selection is enabled, store the date and show time picker
      setTempDate(selectedDate);
      setShowTimePicker(true);
      setTimeView("hours");
    } else { // If time selection is NOT enabled, set the date directly with default time
      if (selecting === "start") {
        handleStartDate(selectedDate.startOf('day')); // Set start of day for start date
        setSelecting("end");
      } else {
        if (selectedDate.isBefore(dayjs(startDateReFormat), "day")) {
          alert("End date cannot be before start date.");
          return;
        }
        handleEndDate(selectedDate.endOf('day')); // Set end of day for end date
        setOpen(false);
        setSelecting("start");
      }
    }
  };

  const handleTimeSelect = (dateTime) => {
    if (!tempDate) return;

    const combinedDateTime = tempDate
      .hour(dateTime.hour())
      .minute(dateTime.minute())
      .second(0)
      .millisecond(0);

    if (selecting === "start") {
      handleStartDate(combinedDateTime);
      setSelecting("end");
    } else {
      if (combinedDateTime.isBefore(dayjs(startDateReFormat))) {
        alert("End date and time cannot be before start date and time.");
        return;
      }
      handleEndDate(combinedDateTime);
      setOpen(false);
      setSelecting("start");
    }

    setShowTimePicker(false);
    setTempDate(null);
    setTimeView("hours");
  };

  const handleReset = () => {
    setShowTimePicker(false);
    setTempDate(null);
    handleStartDate(null);
    handleEndDate(null);
    setSelecting("start");
    setTimeView("hours");
    setOpen(false);
  };

  useEffect(() => {
    // Set default dates only if they are not already set
    if (!startDateReFormat && !endDateReFormat) {
      const defaultEndDate = dayjs().endOf('day'); // Default end date to end of day
      const defaultStartDate = dayjs().subtract(0, "day").startOf('day'); // Default start date to start of day

      handleStartDate(defaultStartDate);
      handleEndDate(defaultEndDate);
    }
  }, [startDateReFormat, endDateReFormat, handleStartDate, handleEndDate]);

  useEffect(() => {
    if (selecting === "start" && startDateReFormat) {
      setCalendarMonth(dayjs(startDateReFormat));
    } else if (selecting === "end" && endDateReFormat) {
      setCalendarMonth(dayjs(endDateReFormat));
    }
  }, [selecting, startDateReFormat, endDateReFormat]);

  const minDateForEndDate =
    selecting === "end" && startDateReFormat
      ? dayjs(startDateReFormat)
      : undefined;

  const timePickerValue =
    tempDate || (selecting === "start" ? startDateReFormat : endDateReFormat);

  return (
    <>
      <Box sx={{ width: "360px" }}>
        <TextField
          fullWidth
          value={formatDateRange()}
          placeholder="Date Range"
          onClick={() => setOpen(true)}
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#fff",
              "& .MuiInputBase-input": {
                padding: "8px 12px",
                cursor: "pointer",
              },
            },
          }}
        />
        {open && (
          <ClickAwayListener
            onClickAway={() => {
              if (!showTimePicker || (showTimePicker && !tempDate)) {
                setOpen(false);
                setShowTimePicker(false);
                setSelecting("start");
                setTempDate(null);
                setTimeView("hours");
              }
            }}
          >
            <Box
              sx={{
                position: "absolute",
                zIndex: 1000,
                backgroundColor: "#fff",
                boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                borderRadius: "4px",
                mt: 1,
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ mb: 1, textAlign: "center" }}>
                    {selecting === "start"
                      ? "Select Start Date"
                      : "Select End Date"}
                  </Box>
                  {!showTimePicker ? (
                    <DateCalendar
                      value={calendarMonth}
                      onChange={(newDate) => {
                        setCalendarMonth(newDate); // Keep current month in state
                        handleDateSelect(newDate);
                      }}
                      disableFuture
                      minDate={minDateForEndDate}
                    />
                  ) : (
                    <Box>
                      <StaticTimePicker
                        value={timePickerValue}
                        onChange={handleTimeSelect}
                        view={timeView}
                        onViewChange={(newView) => setTimeView(newView)}
                        ampm={true}
                        ampmInClock={true}
                        slots={{
                          digitClock: () => null,
                          toolbar: () => null,
                          actionBar: () => null,
                        }}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
                  >
                    <Button
                      onClick={() => {
                        setOpen(false);
                        setShowTimePicker(false);
                        setSelecting("start");
                        setTempDate(null);
                        setTimeView("hours");
                      }}
                      sx={{ color: "#2196f3" }}
                    >
                      CANCEL
                    </Button>
                    <Button onClick={handleReset} sx={{ color: "#2196f3" }}>
                      RESET
                    </Button>
                  </Box>
                </Box>
              </LocalizationProvider>
            </Box>
          </ClickAwayListener>
        )}
      </Box>
    </>
  );
};

export default TimeDatePicker;